import type { OpenAPI } from "simplydo/interfaces";
import { Divider, Icon, Label, List } from "simplydo/react/ui";

import util from "utils/utils";

import CircularProgress from "components/lib/CircularProgress";
import type { SideNavItemProps } from "components/lib/SideNav/SideNavComponent";

import {
  FieldEntry,
  FieldTitleClamp,
  PercentIcon,
} from "./AssessmentSectionEntry.styles";

type AssessmentSectionEntryProps = {
  assessment: NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number];
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number];
  activeSectionId: string | null;
  scrollToSection: (id: string) => void;
  activeFieldId: string | null;
  scrollToField: (id: string) => void;
};

export const createAssessmentSectionEntry = ({
  assessment,
  section,
  activeSectionId,
  scrollToSection,
  activeFieldId,
  scrollToField,
}: AssessmentSectionEntryProps): SideNavItemProps[] => {
  const sectionName = section.name ?? "";

  if (!assessment) {
    return [];
  }

  if (
    assessment.conflictOfInterest?.hasConflictOfInterest === undefined ||
    assessment.conflictOfInterest?.hasConflictOfInterest === true
  ) {
    return [];
  }
  if (
    (section.assessmentRoundId || "legacy") !==
    (assessment.assessmentRoundId || "legacy")
  ) {
    return [];
  }

  const missingRequiredFields: NonNullable<
    OpenAPI.Schemas["IdeaTemplate"]["body"]
  >[number]["fields"] = [];
  let requiredFields = 0;
  let completedFields = 0;
  for (const field of section.fields) {
    if (!util.isAssessableField(field)) {
      continue;
    }
    requiredFields += 1;
    if (util.assessmentFieldIsComplete(assessment.assessment, field)) {
      completedFields += 1;
    } else {
      missingRequiredFields.push(field);
    }
  }

  const color = requiredFields === completedFields ? "#28a745" : "#dc3545";

  let tooltip: string = "";
  if (missingRequiredFields?.length > 0) {
    tooltip = `${missingRequiredFields.length} required`;
  }
  tooltip =
    tooltip ?
      `${tooltip} ${util.pluralise(requiredFields - completedFields, "question", "questions", false)} remaining`
    : "Section completed!";

  return [
    {
      key: `${sectionName}-${section.id}`,
      type: "link",
      content: (
        <div style={{ paddingRight: 8 }}>
          {sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}
        </div>
      ),
      tooltip: (
        <div>
          {tooltip}
          {missingRequiredFields.length > 0 ?
            <>
              <Divider />
              <b>
                Required{" "}
                {util.pluralise(
                  missingRequiredFields.length,
                  "question",
                  "questions",
                  false,
                )}{" "}
                remaining:
              </b>
              <div style={{ padding: 8, maxHeight: 300, overflow: "auto" }}>
                <List bulleted>
                  {missingRequiredFields?.map((field) => (
                    <List.Item key={field.id}>
                      <FieldTitleClamp>
                        {field.title || "Untitled"}
                      </FieldTitleClamp>
                    </List.Item>
                  ))}
                </List>
              </div>
            </>
          : null}
        </div>
      ),
      iconLeft: (
        <PercentIcon>
          <Icon
            style={{ height: 10, margin: 0, color: "#fff" }}
            size="small"
            name="percent"
          />
        </PercentIcon>
      ),
      rightIcon:
        requiredFields > 0 ?
          <CircularProgress
            size={16}
            color={color}
            progress={completedFields / requiredFields}
            strokeWidth={1.5}
            icon={<Icon name="check" style={{ color }} />}
            iconVisible={
              requiredFields === completedFields &&
              (missingRequiredFields.length || 0) === 0
            }
          />
        : undefined,
      active: section.type === "assessment" && activeSectionId === section.id,
      onClick: () => scrollToSection(section.id),
    },
    ...section.fields.map((field) => {
      const assessmentField = assessment.assessment?.[field.id];
      const isComplete = util.assessmentFieldIsComplete(
        assessment.assessment,
        field,
      );

      if (field.type === "infotext") {
        return {
          key: `${sectionName}-${field.id}`,
          type: "link" as const,
          content: (
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-between",
                marginLeft: 28,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "inherit",
                  flex: 1,
                  gap: 4,
                }}
              >
                <Icon name="info circle" size="small" />
                <FieldEntry>Supporting information</FieldEntry>
              </div>
            </div>
          ),
          active: activeSectionId === section.id && activeFieldId === field.id,
          onClick: () => scrollToField(field.id),
        };
      }

      return {
        key: `${sectionName}-${field.id}`,
        type: "link" as const,
        tooltip:
          isComplete ? undefined : (
            <div style={{ maxHeight: 300, overflow: "auto" }}>
              {field.title || "Untitled question"}
              {field.description ?
                <>
                  <br />
                  <br />
                  {field.description}
                </>
              : null}
            </div>
          ),
        content: (
          <div
            style={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              justifyContent: "space-between",
              marginLeft: 28,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "inherit",
                flex: 1,
                gap: 4,
              }}
            >
              <Icon name="arrow right" size="small" />
              <FieldEntry>{field.title || "Untitled question"}</FieldEntry>
            </div>
            <Label size="mini" color={isComplete ? "green" : "orange"}>
              {isComplete ?
                <div>
                  {assessmentField?.score ?
                    <span>
                      {assessmentField?.score}/
                      {!Array.isArray(field.options) ?
                        field.options?.availableScore
                      : 0}
                    </span>
                  : <div
                      style={{
                        maxWidth: 75,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field.type === "assessmentFeedback" ?
                        <Icon name="check" style={{ margin: 0 }} />
                      : assessmentField?.value}
                    </div>
                  }
                </div>
              : <Icon name="warning sign" style={{ margin: 0 }} />}
            </Label>
          </div>
        ),
        active: activeSectionId === section.id && activeFieldId === field.id,
        onClick: () => scrollToField(field.id),
      };
    }),
  ];
};
